// Uncaught browser errors (window `error` and `unhandledrejection`) to the
// Datadog logs, bounded so one page can never flood them (staging audit 1).
//
// Why not the SDK's `forwardErrorsToLogs`: besides uncaught errors it forwards
// every `console.error` (about 80 call sites in this app) and every failed or
// 5xx request, and it wraps `console.error` and `fetch` to do so. That is what
// was turned off in Feb 2025 ("Prevent sending errors to Datadog"). Here only
// uncaught errors go, each distinct message once, at most `max` a page.

/** At most this many uncaught-error lines a page load. */
export const MAX_UNCAUGHT_ERRORS_PER_PAGE = 10

/**
 * The log line's name. A constant, not a `message: '…'` literal: the i18n
 * extractor (`extractKeys.js`) reads those as copy keys.
 */
export const UNCAUGHT_ERROR_LOG = 'uncaught_error'

export interface UncaughtErrorLine {
  context: {
    user_event: {
      type: 'error' | 'unhandledrejection'
      message: string
      /** `file:line:col` of a thrown error, when the browser gives it. */
      source?: string
    }
  }
  /** The thrown value when it is an Error: the SDK reads its kind and stack. */
  error?: Error
}

type ErrorEventLike = Pick<ErrorEvent, 'message' | 'filename' | 'lineno' | 'colno'> & {
  error?: unknown
}
interface RejectionEventLike {
  reason?: unknown
}

export interface UncaughtErrorForwarder {
  /** For `window`'s `error` event. */
  onError: (event: ErrorEventLike) => void
  /** For `window`'s `unhandledrejection` event. */
  onRejection: (event: RejectionEventLike) => void
}

const asError = (value: unknown): Error | undefined => (value instanceof Error ? value : undefined)

function describeValue(value: unknown): string {
  if (value instanceof Error) return value.message || value.name
  if (typeof value === 'object' && value !== null) {
    try {
      return JSON.stringify(value)
    } catch {
      return String(value)
    }
  }
  return String(value)
}

export function createUncaughtErrorForwarder(
  send: (line: UncaughtErrorLine) => void,
  max: number = MAX_UNCAUGHT_ERRORS_PER_PAGE
): UncaughtErrorForwarder {
  const seen: Record<string, true> = {}
  let count = 0

  function forward(
    type: UncaughtErrorLine['context']['user_event']['type'],
    message: string,
    error: Error | undefined,
    source?: string
  ): void {
    const key = `${type}:${message}`
    if (seen[key] || count >= max) return
    seen[key] = true
    count += 1
    send({
      context: { user_event: { type, message, ...(source ? { source } : {}) } },
      ...(error ? { error } : {})
    })
  }

  return {
    onError: (event) => {
      const error = asError(event.error)
      const message = error ? describeValue(error) : event.message || 'unknown error'
      const source = event.filename ? `${event.filename}:${event.lineno}:${event.colno}` : undefined
      forward('error', message, error, source)
    },
    onRejection: (event) => {
      forward('unhandledrejection', describeValue(event.reason), asError(event.reason))
    }
  }
}
